import { useEffect, useState } from "react";
import type { FileDTO } from "../../../shared/types";
import { FileCard, FileSharingBadge, fileUrl, formatBytes } from "../../components/Files";
import { Dialog } from "../../components/Dialog";
import { MoreOnScroll, usePaged } from "../../components/MoreOnScroll";
import { Table } from "../../components/Table";
import { TextLink } from "../../components/TextLink";
import { Badge, ErrorText, inputCls, PageHeader, timeAgo } from "../../components/ui";
import { get } from "../../lib/api";
import { navigate, useLocation } from "../../lib/router";

/**
 * Every file, for administration (security plan D16): its name, owner, who may open it, size and when. An admin opens
 * only what is shared with them or belongs to an agent they own, like anyone; the rest is its details only.
 */
export function AdminFiles() {
  const { pathname, search } = useLocation();
  const q = search.get("q") ?? "";
  const [draft, setDraft] = useState(q);
  const [selected, setSelected] = useState<FileDTO | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (draft === q) return;
      navigate(`${pathname}${draft.trim() ? `?${new URLSearchParams({ q: draft.trim() })}` : ""}`, true);
    }, 300);
    return () => clearTimeout(timer);
  }, [draft]);

  const { rows, done, loading, error, more } = usePaged<FileDTO>(
    (before) => get<FileDTO[]>(`/api/files?${new URLSearchParams({ all: "1", ...(q ? { q } : {}), ...(before ? { before } : {}) })}`),
    [q],
  );

  return (
    <div>
      <PageHeader title="Files" subtitle="Everything uploaded by people or made by agents. Each opens only for whoever it is shared with; every download is audited." />
      <div className="px-4 py-4 md:px-6">
        <Table
          rows={rows ?? []}
          rowKey={(f) => f.id}
          onRowClick={setSelected}
          filters={<input className={`${inputCls} max-w-sm`} placeholder="Search by name…" value={draft} onChange={(e) => setDraft(e.target.value)} />}
          empty={rows === null ? "Loading…" : q ? `No file named like “${q}”.` : "No files yet."}
          columns={[
            { header: "Name", primary: true, cell: (f) => <div className="truncate font-medium">{f.name}</div> },
            { header: "Source", cell: (f) => <Badge tone={f.source === "agent" ? "blue" : "zinc"}>{f.kept ? `${f.source}, kept` : f.source}</Badge> },
            { header: "Owner", className: "text-muted", cell: (f) => f.ownerName ?? "—" },
            { header: "Who can open it", cell: (f) => <FileSharingBadge file={f} /> },
            { header: "Size", className: "text-muted", cell: (f) => formatBytes(f.size) },
            { header: "When", className: "text-xs text-muted", cell: (f) => timeAgo(f.createdAt) },
            {
              header: "",
              align: "right",
              cell: (f) =>
                f.canOpen ? (
                  <TextLink href={fileUrl(f.id)} onClick={(e) => e.stopPropagation()} className="text-xs">download</TextLink>
                ) : (
                  <span className="text-xs text-faint">not shared with you</span>
                ),
            },
          ]}
        />
        {error && <ErrorText error={error} />}
        <MoreOnScroll onMore={more} done={done} loading={loading} />
      </div>
      <Dialog open={Boolean(selected)} onClose={() => setSelected(null)} size="lg" title={selected?.name}>
        {selected && (
          <div className="space-y-3 text-sm">
            <FileCard file={selected} />
            <dl className="grid grid-cols-[7rem_1fr] gap-y-1 text-xs">
              <dt className="text-muted">Type</dt><dd className="font-mono">{selected.mime}</dd>
              <dt className="text-muted">SHA-256</dt><dd className="font-mono break-all">{selected.sha256}</dd>
              <dt className="text-muted">Owner</dt><dd>{selected.ownerName ?? "—"}</dd>
              <dt className="text-muted">Who can open it</dt><dd><FileSharingBadge file={selected} /></dd>
              {selected.conversationId && (<><dt className="text-muted">Conversation</dt><dd><TextLink to={`/admin/conversations/${selected.conversationId}`}>open</TextLink></dd></>)}
              {selected.runId && (<><dt className="text-muted">Run</dt><dd><TextLink to={`/admin/runs/${selected.runId}`}>open</TextLink></dd></>)}
            </dl>
          </div>
        )}
      </Dialog>
    </div>
  );
}
